import { isSelectEntryEdited, isTextFieldEntryEdited, ListGroup } from '@bpmn-io/properties-panel';
import { useCallback, useMemo } from '@bpmn-io/properties-panel/preact/hooks';
import { getBusinessObject, is, isAny } from 'bpmn-js/lib/util/ModelUtil';
import { getExtensionElementsList } from 'camunda-bpmn-js/lib/util/ExtensionElementsUtil';
import { without } from 'min-dash';
import type { EntryDefinition, GroupFn, ListItemDefinition } from '../../../types/properties-panel';
import { useService } from '../hooks/useService';
import { createElement } from '../util/factory-util';
import { MailRecipient } from './MailRecipient';
import { MailRecipientFields } from './MailRecipientFields';
import { TextWithFieldRef } from './TextWithFieldRef';
import { createOption, SelectEntry } from './SelectEntry';
import { TextFieldEntry } from './TextFieldEntry';

const groupId = 'mailsGroup';

export const TaskMailsGroup: GroupFn = (element, injector) => {
    if (
        !is(element, 'bpmn:UserTask') &&
        isAny(element, ['bpmn:Task', 'bpmn:ServiceTask', 'bpmn:ScriptTask' /*, 'bpmn:UserTask'*/])
    ) {
        const translate = injector.get('translate');

        return {
            add: (event) => {
                event.stopPropagation();
                const bpmnFactory = injector.get('bpmnFactory');
                const commands = [];
                const businessObject = getBusinessObject(element);
                let extensionElements = businessObject.extensionElements;

                // (1) ensure extension elements
                if (!extensionElements) {
                    extensionElements = createElement(
                        bpmnFactory,
                        'bpmn:ExtensionElements',
                        { values: [] },
                        businessObject
                    );

                    commands.push({
                        cmd: 'element.updateModdleProperties',
                        context: {
                            element,
                            moddleElement: businessObject,
                            properties: { extensionElements },
                        },
                    });
                }

                // (2) ensure mails extension
                let extension = getExtensionElementsList(element, 'fism:Mails')[0];

                if (!extension) {
                    extension = createElement(
                        bpmnFactory,
                        'fism:Mails',
                        { values: [] },
                        extensionElements
                    );

                    commands.push({
                        cmd: 'element.updateModdleProperties',
                        context: {
                            element,
                            moddleElement: extensionElements,
                            properties: {
                                values: [...extensionElements.get('values'), extension],
                            },
                        },
                    });
                }

                // (3) create and add mail extension
                commands.push({
                    cmd: 'element.updateModdleProperties',
                    context: {
                        element,
                        moddleElement: extension,
                        properties: {
                            values: [
                                ...extension.get('values'),
                                createElement(
                                    bpmnFactory,
                                    'fism:Mail',
                                    {
                                        id: injector.get('moddle').ids.nextPrefixed('Mail_'),
                                    },
                                    extension
                                ),
                            ],
                        },
                    },
                });

                injector
                    .get('commandStack')
                    .execute('properties-panel.multi-command-executor', commands);
            },
            element,
            id: groupId,
            // items: [],
            items: (
                (getExtensionElementsList(element, 'fism:Mails')[0]?.values as any[]) ?? []
            ).map<ListItemDefinition>((item) => {
                const id = `${element.id}-${item.id}`;
                return {
                    id,
                    entries: [
                        {
                            component: NameComp,
                            id: id + '-name',
                            isEdited: isTextFieldEntryEdited,
                            moddleElement: item,
                        },
                        {
                            component: MailRecipient,
                            id: id + '-sendto',
                            label: `${translate('Recipient')} (${translate('Email')})`,
                            moddleElement: item,
                            type: 'to',
                        },
                        {
                            component: MailRecipientFields,
                            id: id + '-sendtofield',
                            label: `${translate('Recipient')} (${translate('Field')})`,
                            moddleElement: item,
                            type: 'to',
                        },
                        {
                            component: MailRecipient,
                            id: id + '-copyto',
                            label: `CC (${translate('Email')})`,
                            moddleElement: item,
                            type: 'cc',
                        },
                        {
                            component: MailRecipientFields,
                            id: id + '-copytofield',
                            label: `CC (${translate('Field')})`,
                            moddleElement: item,
                            type: 'cc',
                        },
                        {
                            attrPropName: 'subject',
                            valPropName: 'subject',
                            component: TextWithFieldRef,
                            id: id + '-subject',
                            label: translate('Subject'),
                            // isEdited: isTextFieldEntryEdited,
                            moddleElement: item,
                            type: 'fism:MailSubject',
                        },
                        {
                            attrPropName: 'body',
                            valPropName: 'body',
                            component: TextWithFieldRef,
                            id: id + '-body',
                            label: translate('Message'),
                            // isEdited: isTextFieldEntryEdited,
                            moddleElement: item,
                            multiline: true,
                            type: 'fism:MailBody',
                        },
                        {
                            component: ExecuteOnComp,
                            id: id + '-executeOn',
                            isEdited: isSelectEntryEdited,
                            moddleElement: item,
                        },
                        /*{
                            component: ActionComp,
                            id: id + '-action',
                            isEdited: isSelectEntryEdited,
                            moddleElement: item,
                        },
                        ...entriesForType(id, item),*/
                    ] as any,
                    label: item.name || translate('<empty>'),
                    remove: (event) => {
                        event.stopPropagation();
                        const extension = getExtensionElementsList(element, 'fism:Mails')[0];
                        injector.get('modeling').updateModdleProperties(element, extension, {
                            values: without(extension.values, item),
                        });
                    },
                    autoOpen: true,
                    autoFocusEntry: id + '-name',
                };
            }),
            label: translate('Email dispatch'),
            component: ListGroup,
        };
    }
};

type SpGroupComp<E = {}> = EntryDefinition<{}, E>['component'];
type TCompProps<E = {}> = ListItemDefinition['entries'][number] & E;

const NameComp: SpGroupComp<TCompProps> = (props) => {
    const { element, moddleElement } = props;
    const modeling = useService('modeling');
    const translate = useService('translate');

    return TextFieldEntry({
        debounce: useService('debounceInput'),
        element,
        getValue: () => moddleElement.name || '',
        id: props.id,
        label: translate('Name'),
        setValue: (value) => {
            modeling.updateModdleProperties(element, moddleElement, { name: value });
        },
    });
};

const ExecuteOnComp: SpGroupComp<TCompProps> = (props) => {
    const { element, moddleElement } = props;
    const modeling = useService('modeling');
    const translate = useService('translate');
    const value = moddleElement.executeOn || '';

    const options = useMemo(
        () => [
            createOption(translate('Entering'), 'entering'),
            createOption(translate('Leaving'), 'leaving'),
        ],
        []
    );

    return SelectEntry({
        element,
        // disabled: loading,
        // description: loading ? translate('Loading...') : undefined,
        getOptions: useCallback(
            () =>
                value
                    ? options
                    : [createOption(translate('Choose from'), '', true)].concat(options),
            [options, value]
        ),
        getValue: () => value,
        id: props.id,
        label: translate('Send on'),
        setValue: (newValue) => {
            modeling.updateModdleProperties(element, moddleElement, { executeOn: newValue });
        },
        /*validate: value => {
            console.log('### validate', value);
            if (!value) {
                return 'Pflichtfeld';
            }
        }*/
    });
};
